<template>
    <div class="mesh-view-container">
        <!-- CONTENEDOR DEL MAPA SATELITAL -->
        <div ref="wrapperRef" class="topology-wrapper">
            <div id="mapMeshContainer" class="mapMeshContainer"></div>

            <v-btn
                icon
                size="small"
                color="slate-800"
                class="fullscreen-btn"
                @click="toggleFullscreen"
            >
                <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
        </div>

        <!-- SECCIÓN INFERIOR: EQUIPOS SIN UBICACIÓN GPS -->
        <div v-if="equiposSinGps.length > 0" class="no-gps-section mt-2 pa-2">
            <div class="d-flex align-center mb-2 ga-2">
                <v-icon size="x-small" color="warning">mdi-map-marker-off</v-icon>
                <span class="no-gps-title font-weight-bold">
                    Equipos sin ubicación GPS
                </span>
                <v-chip size="x-small" color="grey-darken-2" variant="flat" class="px-1" style="height: 16px; font-size: 0.65rem;">
                    {{ equiposSinGps.length }}
                </v-chip>
            </div>

            <div class="no-gps-scroll-container d-flex ga-2 pb-1">
                <div
                    v-for="equipo in equiposSinGps"
                    :key="equipo.ip"
                    class="no-gps-card pa-1 px-2"
                    @click="$emit('select-device', equipo)"
                >
                    <!-- BARRA DE OPERATIVIDAD PORCENTUAL -->
                    <div class="operability-bar-wrapper mb-1">
                        <div class="operability-stacked-bar">
                            <div
                                class="bar-segment"
                                style="background-color: #00E676;"
                                :style="{ width: getOperabilityPercentages(equipo.ip).ok + '%' }"
                                :title="`Excelente estado: ${getOperabilityPercentages(equipo.ip).ok.toFixed(1)}%`"
                            ></div>
                            <div
                                class="bar-segment"
                                style="background-color: #FFEA00;"
                                :style="{ width: getOperabilityPercentages(equipo.ip).alert + '%' }"
                                :title="`Alerta leve: ${getOperabilityPercentages(equipo.ip).alert.toFixed(1)}%`"
                            ></div>
                            <div
                                class="bar-segment"
                                style="background-color: #FF3D00;"
                                :style="{ width: getOperabilityPercentages(equipo.ip).alarm + '%' }"
                                :title="`Alarma crítica: ${getOperabilityPercentages(equipo.ip).alarm.toFixed(1)}%`"
                            ></div>
                            <div
                                class="bar-segment"
                                style="background-color: #9E9E9E;"
                                :style="{ width: getOperabilityPercentages(equipo.ip).down + '%' }"
                                :title="`Fuera de servicio: ${getOperabilityPercentages(equipo.ip).down.toFixed(1)}%`"
                            ></div>
                        </div>
                    </div>

                    <!-- DATOS DEL EQUIPO Y LATENCIA -->
                    <div class="d-flex justify-space-between align-center ga-2">
                        <span class="device-name font-weight-bold text-truncate" :style="{ color: getLatencyColor(equipo.latencia) }">
                            {{ equipo.name }}
                        </span>
                        <span class="latency-badge font-weight-bold" :style="{ color: getLatencyColor(equipo.latencia), borderColor: getLatencyColor(equipo.latencia) }">
                            {{ equipo.latencia < 0 ? 'OFF' : `${equipo.latencia}ms` }}
                        </span>
                    </div>

                    <!-- MODELO Y IP -->
                    <div class="d-flex justify-space-between align-center mt-1 style-details">
                        <span class="device-model text-truncate">{{ getDeviceModel(equipo.anotacion) }}</span>
                        <span class="ip-text">{{ equipo.ip }}</span>
                    </div>

                    <!-- FECHA DE ACTUALIZACIÓN -->
                    <div class="d-flex justify-end align-center mt-1">
                        <span class="date-text">Upd: {{ formatShortDate(equipo.fecha) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import maplibregl, { LngLatLike, Map, Popup, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import CONSTMAPS from "@/constants/maps";
// import { LATENCY } from "@/interfaces/SmartLink/Topology";
import { getDeviceModel } from "@/utils/SmartLink/device";

const props = defineProps<{
    items: any[];
    getLatencyColor: (lat: number) => string;
    getOperabilityPercentages: (ip: string) => any;
    formatShortDate: (date?: string) => string;
}>();

const emit = defineEmits(["select-device"]);

const wrapperRef = ref<HTMLElement | null>(null);
const isFullscreen = ref(false);
let mapInstance: Map | null = null;
let activePopup: Popup | null = null;
let currentMarkers: Marker[] = [];

const centerMap = ref<LngLatLike>([
    CONSTMAPS.longitud.value,
    CONSTMAPS.latitud.value,
]);

// Deduplicar la lista total de items
const uniqueItems = computed(() => {
    return props.items.filter(
        (item, index, self) => index === self.findIndex((t) => t.ip === item.ip)
    );
});

// Separación A: Equipos con coordenadas válidas para dibujar en el mapa
const equiposConGps = computed(() => {
    return uniqueItems.value.filter(
        (e) =>
            e.latitud !== undefined &&
            e.longitud !== undefined &&
            Number(e.latitud) !== -1 &&
            Number(e.longitud) !== -1 &&
            Number(e.latitud) !== 0 &&
            Number(e.longitud) !== 0
    );
});

// Separación B: Equipos sin coordenadas válidas (-1, 0 o indefinidos)
const equiposSinGps = computed(() => {
    return uniqueItems.value.filter(
        (e) =>
            e.latitud === undefined ||
            e.longitud === undefined ||
            Number(e.latitud) === -1 ||
            Number(e.longitud) === -1 ||
            Number(e.latitud) === 0 ||
            Number(e.longitud) === 0
    );
});

const clearMarkers = () => {
    currentMarkers.forEach((marker) => marker.remove());
    currentMarkers = [];
};

const renderMapMarkers = () => {
    if (!mapInstance) return;

    clearMarkers();

    equiposConGps.value.forEach((equipo) => {
        const latColor = props.getLatencyColor(equipo.latencia);
        const modelText = getDeviceModel(equipo.anotacion);
        const dateText = props.formatShortDate(equipo.fecha);
        const op = props.getOperabilityPercentages(equipo.ip);
        const latDisplay = equipo.latencia < 0 ? "OFF" : `${equipo.latencia}ms`;

        const el = document.createElement("div");
        el.className = "custom-map-marker";

        el.innerHTML = `
            <div class="marker-op-box">
                <div class="marker-op-bar">
                    <div class="op-seg" style="width: ${op.ok}%; background-color: #00E676;"></div>
                    <div class="op-seg" style="width: ${op.alert}%; background-color: #FFEA00;"></div>
                    <div class="op-seg" style="width: ${op.alarm}%; background-color: #FF3D00;"></div>
                    <div class="op-seg" style="width: ${op.down}%; background-color: #9E9E9E;"></div>
                </div>
            </div>
            <div class="marker-text-content">
                <div class="marker-name" style="color: ${latColor};">${equipo.name}</div>
                <div class="marker-model-lat" style="color: ${latColor};">
                    ${modelText} (${latDisplay})
                </div>
            </div>
        `;

        el.addEventListener("click", (e) => {
            e.stopPropagation();
            emit("select-device", equipo);
        });

        el.addEventListener("mouseenter", () => {
            if (!mapInstance) return;

            const htmlContent = `
                <div style="font-family: sans-serif; padding: 4px; font-size: 0.78rem; color: #0F172A;">
                    <b style="font-size: 0.85rem; color: #0F172A;">${equipo.name}</b><br/>
                    <span style="color: #0284C7;"><b>Modelo:</b> ${modelText}</span><br/>
                    <span><b>IP:</b> ${equipo.ip}</span><br/>
                    <span><b>Latencia:</b> ${latDisplay}</span><br/>
                    <span><b>Última act.:</b> ${dateText}</span>
                    <hr style="border-top: 1px solid #CBD5E1; margin: 4px 0;" />
                    <b>Operatividad:</b><br/>
                    <span style="color: #16A34A;">● Excelente estado: ${op.ok.toFixed(1)}%</span><br/>
                    <span style="color: #CA8A04;">● Alerta leve: ${op.alert.toFixed(1)}%</span><br/>
                    <span style="color: #DC2626;">● Alarma crítica: ${op.alarm.toFixed(1)}%</span><br/>
                    <span style="color: #64748B;">● Fuera de servicio: ${op.down.toFixed(1)}%</span>
                </div>
            `;

            if (activePopup) activePopup.remove();
            activePopup = new maplibregl.Popup({ closeButton: false, offset: 15 })
                .setLngLat([Number(equipo.longitud), Number(equipo.latitud)])
                .setHTML(htmlContent)
                .addTo(mapInstance);
        });

        el.addEventListener("mouseleave", () => {
            if (activePopup) {
                activePopup.remove();
                activePopup = null;
            }
        });

        const marker = new maplibregl.Marker({ element: el })
            .setLngLat([Number(equipo.longitud), Number(equipo.latitud)])
            .addTo(mapInstance);

        currentMarkers.push(marker);
    });
};

watch(
    () => props.items,
    () => {
        renderMapMarkers();
    },
    { deep: true }
);

function inicializarMapa() {
    mapInstance = new maplibregl.Map({
        container: "mapMeshContainer",
        center: centerMap.value,
        zoom: 14,
        pitch: 35,
        bearing: 20,
        style: "https://api.maptiler.com/maps/satellite/style.json?key=6JWVt6LFaY22nquimZpF",
    });

    mapInstance.addControl(new maplibregl.NavigationControl(), "top-right");

    mapInstance.on("load", () => {
        setTimeout(() => {
            if (mapInstance) {
                mapInstance.resize();
                renderMapMarkers();
            }
        }, 300);
    });
}

function toggleFullscreen() {
    if (!wrapperRef.value) return;
    if (!document.fullscreenElement) {
        wrapperRef.value.requestFullscreen().then(() => {
            isFullscreen.value = true;
            nextTick(() => mapInstance?.resize());
        });
    } else {
        document.exitFullscreen().then(() => {
            isFullscreen.value = false;
            nextTick(() => mapInstance?.resize());
        });
    }
}

onMounted(() => {
    nextTick(() => {
        inicializarMapa();
    });
});

onUnmounted(() => {
    clearMarkers();
    if (mapInstance) mapInstance.remove();
});
</script>

<style scoped>
.mesh-view-container {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.topology-wrapper {
    position: relative;
    width: 100%;
    height: 60vh;
    background-color: #06090e;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.mapMeshContainer {
    width: 100% !important;
    height: 100% !important;
}

.fullscreen-btn {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 10;
    background-color: rgba(15, 23, 42, 0.8) !important;
    backdrop-filter: blur(4px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #FFFFFF !important;
}

/* ESTILOS DE LA SECCIÓN INFERIOR (EQUIPOS SIN GPS) */
.no-gps-section {
    background-color: #0c1017;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
}

.no-gps-title {
    font-size: 0.72rem;
    color: #94A3B8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.no-gps-scroll-container {
    overflow-x: auto;
    white-space: nowrap;
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.no-gps-card {
    background-color: #121824;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    min-width: 160px;
    max-width: 180px;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    flex: 0 0 auto;
}

.no-gps-card:hover {
    border-color: rgba(255, 255, 255, 0.35);
    background-color: #1a2232;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.6) !important;
}

.device-name {
    font-size: 0.68rem;
    font-family: monospace;
}

.device-model {
    font-size: 0.58rem;
    color: #94A3B8;
}

.ip-text {
    font-family: monospace;
    font-size: 0.60rem;
    color: #64748B;
}

.latency-badge {
    font-size: 0.60rem;
    padding: 0 3px;
    border-radius: 3px;
    background-color: rgba(0, 0, 0, 0.5);
}

.operability-bar-wrapper {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
    overflow: hidden;
}

.operability-stacked-bar {
    display: flex;
    width: 100%;
    height: 3px;
}

.bar-segment {
    height: 100%;
    transition: width 0.3s ease;
}

/* MARCADORES HTML DEL MAPA */
:deep(.custom-map-marker) {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    user-select: none;
    transform: translate(-50%, -50%);
}

:deep(.marker-op-box) {
    background-color: #0d131f;
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 3px 4px;
    border-radius: 3px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
    width: 110px;
}

:deep(.marker-op-bar) {
    display: flex;
    width: 100%;
    height: 4px;
    border-radius: 2px;
    overflow: hidden;
    background-color: rgba(255, 255, 255, 0.1);
}

:deep(.op-seg) {
    height: 100%;
    transition: width 0.3s ease;
}

:deep(.marker-text-content) {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 2px;
    text-shadow: -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 0 4px #000;
}

:deep(.marker-name) {
    font-family: monospace;
    font-weight: bold;
    font-size: 0.72rem;
    line-height: 1.1;
    text-align: center;
}

:deep(.marker-model-lat) {
    font-family: monospace;
    font-weight: bold;
    font-size: 0.65rem;
    line-height: 1.1;
    text-align: center;
}

.date-text {
    font-size: 0.58rem;
    color: #94A3B8;
}
</style>